import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { Button } from "@/components/ui/button";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ListHeader } from "../layout/ListHeader";
import { SponsoringCreate } from "./SponsoringCreate";
import { SponsoringEdit } from "./SponsoringEdit";
import { SponsoringEmpty } from "./SponsoringEmpty";
import { SponsoringQuickFilters } from "./SponsoringQuickFilters";
import { SponsoringShow } from "./SponsoringShow";
import { SponsoringTable } from "./SponsoringTable";
import { createSponsoringExporter } from "./sponsoringExporter";
import {
  buildQuickFilterValues,
  NO_QUICK_FILTERS,
  readQuickFilters,
} from "./sponsoringFilters";
import {
  buildViewFilter,
  sponsoringViewBasePath,
  sponsoringViewCreateLabel,
  sponsoringViewKind,
  sponsoringViewSearchPlaceholder,
  sponsoringViewStoreKey,
  sponsoringViewTitle,
  type SponsoringView,
} from "./sponsoringViews";
import { defaultLeadStage } from "./stages";
import { useQuickFilterParam } from "./useQuickFilterParam";

/**
 * Sponsorings page: one table with quick filters above it. The same component
 * serves the three overviews (see sponsoringViews.ts): the sponsorings, the
 * balsponsoren and the leads. On the first two, closed stages stay hidden
 * until "Toon gesloten"; the leads overview is restricted to the funnel
 * stages and needs no stage filters of its own.
 */
const SponsoringList = ({
  view = "sponsorings",
}: {
  view?: SponsoringView;
}) => {
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();

  const exporter = useMemo(
    () => createSponsoringExporter(config.sponsoringStages, view),
    [config.sponsoringStages, view],
  );
  const viewFilter = useMemo(
    () => buildViewFilter(view, config),
    [view, config],
  );
  const defaultFilter = useMemo(
    () =>
      view === "leads"
        ? undefined
        : buildQuickFilterValues({ config, quickFilters: NO_QUICK_FILTERS }),
    [config, view],
  );

  if (!identity) return null;

  return (
    <List
      storeKey={sponsoringViewStoreKey[view]}
      disableSyncWithLocation
      title={false}
      filter={viewFilter}
      filterDefaultValues={defaultFilter}
      sort={{ field: "updated_at", order: "DESC" }}
      perPage={25}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      exporter={exporter}
      actions={<SponsoringActions view={view} />}
    >
      <SponsoringLayout view={view} />
    </List>
  );
};

const SponsoringLayout = ({ view }: { view: SponsoringView }) => {
  const config = useConfigurationContext();
  const location = useLocation();
  const basePath = sponsoringViewBasePath[view];
  const matchCreate = matchPath(`${basePath}/create`, location.pathname);
  const matchShow = matchPath(`${basePath}/:id/show`, location.pathname);
  const matchEdit = matchPath(`${basePath}/:id`, location.pathname);
  const { data, isPending, filterValues } = useListContext();

  // A link such as /sponsorings?filter=expired (from the dashboard) selects
  // that quick filter, then the parameter is dropped from the URL
  useQuickFilterParam(basePath);

  const createDefaultValues = useMemo(
    () => ({
      kind: sponsoringViewKind[view],
      ...(view === "leads" ? { stage: defaultLeadStage(config) } : null),
    }),
    [view, config],
  );

  if (isPending) return null;

  // Nothing at all yet (and no search or quick filter narrowing things down):
  // invite the user to add the first sponsoring instead of an empty table
  const quickFilters = readQuickFilters(filterValues);
  const hasQuickFilter = Object.values(quickFilters).some(Boolean);
  const hasSearch = !!filterValues?.q;
  if (!data?.length && !hasQuickFilter && !hasSearch) {
    return (
      <SponsoringEmpty
        basePath={basePath}
        kind={sponsoringViewKind[view]}
        createLabel={sponsoringViewCreateLabel[view]}
        defaultValues={createDefaultValues}
      />
    );
  }

  return (
    <div className="w-full">
      <SponsoringQuickFilters view={view} />
      <SponsoringTable view={view} />
      <SponsoringCreate
        open={!!matchCreate}
        basePath={basePath}
        defaultValues={createDefaultValues}
      />
      <SponsoringEdit
        open={!!matchEdit && !matchCreate}
        id={matchEdit?.params.id}
        basePath={basePath}
      />
      <SponsoringShow
        open={!!matchShow}
        id={matchShow?.params.id}
        basePath={basePath}
      />
    </div>
  );
};

const SponsoringActions = ({ view }: { view: SponsoringView }) => {
  const translate = useTranslate();
  return (
    <ListHeader
      title={translate(sponsoringViewTitle[view])}
      searchPlaceholder={translate(sponsoringViewSearchPlaceholder[view])}
    >
      <ExportButton />
      <Button asChild variant="outline">
        <Link to={`${sponsoringViewBasePath[view]}/create`}>
          <Plus />
          {translate(sponsoringViewCreateLabel[view])}
        </Link>
      </Button>
    </ListHeader>
  );
};

export default SponsoringList;
